<template>
  <aside class="doc-panel" data-testid="authored-document-panel">
    <div class="doc-panel-bar">
      <RouterLink
        v-if="detailRoute"
        :to="{ name: detailRoute, params: { pid: projectId, docId } }"
        class="text-sm hover:underline"
        >{{ t('chat.authored_document.panel_open_full') }}</RouterLink
      >
      <Button
        icon="pi pi-times"
        text
        size="small"
        class="ml-auto"
        :aria-label="t('chat.authored_document.panel_close')"
        @click="emit('close')"
      />
    </div>
    <div class="doc-panel-body">
      <!-- `can-ask` off: this reader is INSIDE the conversation. A button offering
           to open the chat about the document, in the chat, about that document. -->
      <AuthoredDocumentReader :row="row" :pending="pending" :error="error" :can-ask="false" />
    </div>
  </aside>
</template>

<script setup lang="ts">
/**
 * The open document, beside the conversation (MYAC-1162).
 *
 * Presentational, like everything else on this track: the host owns the live
 * subscription and this renders what arrived. The document updating AS the assistant
 * writes into it is the whole point of a panel over a link — that liveness comes from
 * the host's `useAuthoredDocument`, and keeping the read out of here is what lets the
 * dev preview show the panel's chrome without a Firestore document behind it.
 *
 * The bar keeps a route out to the full page. The panel is the convenient view, not
 * the only one, and a document a user wants to keep reading should not be trapped
 * beside a transcript.
 */
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import Button from 'primevue/button'
import AuthoredDocumentReader from '@/components/documents/AuthoredDocumentReader.vue'
import type { AuthoredDocumentContentRow } from '@/composables/useAuthoredDocument'

withDefaults(
  defineProps<{
    projectId: string
    docId: string
    row: AuthoredDocumentContentRow | null
    /** Route name for the full document page in the caller's shell, when there is one. */
    detailRoute?: string
    pending?: boolean
    error?: Error | null
  }>(),
  { detailRoute: undefined, pending: false, error: null },
)

const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
</script>

<style scoped>
.doc-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* Half the split, but never so narrow that a table wraps to nothing. Below the
     breakpoint the caller does not render the panel at all. */
  flex: 1 1 50%;
  max-width: 50%;
  border-left: 1px solid var(--p-content-border-color);
  padding-left: 0.75rem;
}
.doc-panel-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
}
.doc-panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
</style>
